import React from 'react';
import {
  BarChart,
  Bar,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { ScoringPerformanceResponse, OpportunitySignalLift } from '../../services/analyticsService';

const LOW_N_THRESHOLD = 5;

function formatSecondsMaybe(v: number | null | undefined) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const secs = Number(v);
  if (secs < 60) return `${Math.round(secs)}s`;
  return `${(secs / 60).toFixed(1)}m`;
}

function formatLiftMaybe(v: number | null) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return `${v.toFixed(2)}x`;
}

const ScoringPerformancePanel: React.FC<{
  loading: boolean;
  error?: string;
  data: ScoringPerformanceResponse | null;
}> = ({ loading, error, data }) => {
  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-surface p-4">
        <p className="text-sm text-fg-muted">Loading scoring performance…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-300/50 bg-red-50 dark:bg-red-950/30 p-4 text-sm text-red-800 dark:text-red-100">
        {error}
      </div>
    );
  }

  const byBand = data?.by_band ?? {};

  // An all-zero calibration chart reads as a broken widget rather than as "no data
  // yet", so say so explicitly instead of rendering empty axes.
  const generatedTotal = Object.values(byBand).reduce((sum, row) => sum + (row.generated ?? 0), 0);
  if (!data || generatedTotal === 0) {
    return (
      <div className="rounded-xl border border-border bg-surface p-5">
        <h2 className="text-sm font-semibold text-fg-muted uppercase tracking-wide mb-2">Scoring performance</h2>
        <p className="text-sm text-fg-muted">
          No scored suggestions in this window yet. Once leads are scored and worked, this shows how well hot/warm/cold
          bands predict wins, which signals lift replies, and how fast you respond.
        </p>
      </div>
    );
  }

  const calibrationData = (['hot', 'warm', 'cold'] as const).map((band) => {
    const row = byBand[band] ?? { generated: 0, contacted: 0, replied: 0, won: 0 };
    const conversion = row.generated > 0 ? (row.won / row.generated) * 100 : 0;
    return { band, conversion };
  });

  const signalRows = data?.by_signal
    ? (Object.entries(data.by_signal) as Array<[string, OpportunitySignalLift]>)
        .filter(([, row]) => (row.present ?? 0) >= LOW_N_THRESHOLD)
        .sort((a, b) => {
          const liftA = a[1].lift?.win ?? null;
          const liftB = b[1].lift?.win ?? null;
          if (liftA === null && liftB === null) return 0;
          if (liftA === null) return 1;
          if (liftB === null) return -1;
          return liftB - liftA;
        })
    : [];

  const rt = data?.response_times;
  const timeToContact = rt?.time_to_contact ?? { p50: null, p90: null, count: 0 };
  const timeToReply = rt?.time_to_reply ?? { p50: null, p90: null, count: 0 };
  const totalGenerated = Object.values(byBand).reduce((sum, row) => sum + (row.generated ?? 0), 0);
  const totalReplied = Object.values(byBand).reduce((sum, row) => sum + (row.replied ?? 0), 0);
  const replyRate = totalGenerated > 0 ? (totalReplied / totalGenerated) * 100 : null;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-surface p-5">
        <h2 className="text-sm font-semibold text-fg-muted uppercase tracking-wide mb-4">Calibration</h2>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={calibrationData} margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-chart-grid)" />
              <XAxis dataKey="band" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} unit="%" />
              <Tooltip formatter={(v: unknown) => `${Number(v ?? 0).toFixed(1)}%`} />
              <Bar dataKey="conversion" fill="var(--color-chart-series-a)" name="Won / Generated" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-surface p-5">
        <h2 className="text-sm font-semibold text-fg-muted uppercase tracking-wide mb-4">Signal lift</h2>

        {signalRows.length === 0 ? (
          <p className="text-sm text-fg-muted">Not enough data yet (low-n suppression hides thin signals).</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-surface-muted/50 text-left border-b border-border">
                  <th className="px-3 py-2 font-medium text-fg-secondary">Signal</th>
                  <th className="px-3 py-2 font-medium text-right text-fg-secondary">Present</th>
                  <th className="px-3 py-2 font-medium text-right text-fg-secondary">Lift (Reply)</th>
                  <th className="px-3 py-2 font-medium text-right text-fg-secondary">Lift (Win)</th>
                </tr>
              </thead>
              <tbody>
                {signalRows.map(([key, row]) => (
                  <tr key={key} className="border-t border-border">
                    <td className="px-3 py-2 font-medium">{key}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.present}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatLiftMaybe(row.lift?.reply ?? null)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatLiftMaybe(row.lift?.win ?? null)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-surface p-5">
        <h2 className="text-sm font-semibold text-fg-muted uppercase tracking-wide mb-4">Response time</h2>
        {replyRate !== null ? (
          <p className="text-sm text-fg-muted mb-4">
            Reply rate: <span className="font-medium text-fg">{replyRate.toFixed(1)}%</span> ({totalReplied} of{' '}
            {totalGenerated} generated)
          </p>
        ) : null}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-border bg-app/20 p-4">
            <div className="text-xs text-fg-muted uppercase tracking-wide">Time to contact</div>
            <div className="text-2xl font-semibold mt-2">
              {formatSecondsMaybe(timeToContact.p50)}
            </div>
            <div className="text-sm text-fg-muted mt-1">p90: {formatSecondsMaybe(timeToContact.p90)}</div>
          </div>
          <div className="rounded-lg border border-border bg-app/20 p-4">
            <div className="text-xs text-fg-muted uppercase tracking-wide">Time to reply</div>
            <div className="text-2xl font-semibold mt-2">
              {formatSecondsMaybe(timeToReply.p50)}
            </div>
            <div className="text-sm text-fg-muted mt-1">p90: {formatSecondsMaybe(timeToReply.p90)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoringPerformancePanel;

